import { useRef, useState, Suspense, memo, useEffect } from "react";
import ms from "ms";
import styled from "styled-components/macro";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { BoxNavLeft, BoxContent, BoxFooter } from "./Box";
import { useSkipioApi, touchSkipioApi } from "../api";
import Button from "./Button";
import { MediaMessage, SmsMessage, VoicemailMessage } from "./Message";
import useScrollTriggers from "../hooks/use-scroll-triggers";
import Composer, { TMessage } from "./Composer";
import usePlan from "../hooks/use-plan";
import { useNotificationPrevention } from "../hooks/use-message-notifications";
import useMoment from "../hooks/use-moment";
import { MenuOverlay, MenuButtonItem } from "./Menu";
import useLoadAll from "../hooks/use-load-all";
import MilestoneAlert from "./milestones/MilestoneAlert";
import Title from "./Title";
import { useSkipioQuery } from "../hooks/use-query";
import { Birthday, Eye, MegaPhone, Reply, SkipioLogo } from "./Icons";
import formatPhoneNumber from "../util/formatPhoneNumber";
import Loader from "./Loader";
import { fetchUser } from "../api/users";
import { fetchMessages, Message } from "../api/messages";
import { Milestone } from "../api/milestones";
import { useInView } from "react-intersection-observer";

export default function ContactMessages({ contactId }: { contactId: string }) {
  const moment = useMoment();
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const planLimits = usePlan();
  const contact = useSkipioQuery({ queryKey: ["contacts", contactId] }).data;
  const plan = usePlan();
  const [showMilestone, setShowMilestone] = useState(false);
  const [lastMilestone, setLastMilestone] = useState<Milestone | null>(null);

  const initialState: TMessage = {
    body: "",
    deliverAt: undefined,
    imageUrl: "",
    imageType: undefined,
    voicemailId: null,
  };

  const [value, onChange] = useState(initialState);

  const isSingleScheduledMessage = value.deliverAt;

  const isOverLimit =
    // Can't send scheduled messages to a contact
    (isSingleScheduledMessage && !planLimits.canSendMoreScheduledMessages) ||
    plan.features.paused;

  const isTooLong = value.body && value.body.length > user.maxCharacters;

  const canSend =
    ((!!value.body && value.body.length > 0) ||
      !!value.imageUrl ||
      !!value.voicemailId) &&
    !isOverLimit &&
    !isTooLong;

  async function sendMessage() {
    if (!canSend) return;

    const data = {
      recipients: [`contact-${contact.id}`],
      message: {
        ...(value.voicemailId
          ? {
              voicemailId: value.voicemailId,
              messageType: "VoicemailMessage",
            }
          : {
              messageType: "SmsMessage",
            }),
        body: value.body,
        mediaUrls: value.imageUrl ? [value.imageUrl] : undefined,
        mediaMimeTypes: value.imageUrl ? [value.imageType] : undefined,
        deliverAt: value.deliverAt
          ? new Date(value.deliverAt).toISOString()
          : undefined,
      },
    };

    await skipioApi.messages.post(
      {},
      {
        data,
      }
    );

    await queryClient.invalidateQueries({ queryKey: ["messages"] });
    onChange(initialState);

    // re-fetch plan limits and message lists in the background
    touchSkipioApi("users/me");
    queryClient.invalidateQueries({ queryKey: ["users", "me"] });
    queryClient.invalidateQueries({ queryKey: ["messageThreads"] });
  }

  return (
    <>
      <MilestoneAlert active={showMilestone} fill>
        {!!lastMilestone && <Title>{lastMilestone.name || ""}</Title>}
      </MilestoneAlert>
      <Suspense
        fallback={
          <FullHeight>
            <div>
              <Loader />
            </div>
          </FullHeight>
        }
      >
        <Messages contactId={contactId} />
      </Suspense>
      <BoxFooter
        white
        stacked
        style={{ paddingBottom: 20, paddingTop: 10, flexShrink: 0 }}
      >
        <Composer
          value={value}
          onChange={onChange}
          contactId={contactId}
          enableEventCreation
        />
      </BoxFooter>
      <BoxFooter white style={{ paddingTop: 0, flexShrink: 0 }} stacked>
        <BoxNavLeft>
          <Button onClick={sendMessage} disabled={!canSend}>
            {value.deliverAt
              ? `Send on ${moment(value.deliverAt).format("llll")}`
              : "Send"}
          </Button>
          {plan.isMultiUser && plan.features.teams && (
            <Suspense fallback={null}>
              <MilestoneButton
                contactId={contactId}
                onMilestone={(milestone) => {
                  setShowMilestone(true);
                  setLastMilestone(milestone);
                  setTimeout(() => setShowMilestone(false), 3000);
                }}
              />
            </Suspense>
          )}
        </BoxNavLeft>
      </BoxFooter>
    </>
  );
}

function MilestoneButton({
  contactId,
  onMilestone,
}: {
  contactId: string;
  onMilestone: (milestone: Milestone) => void;
}) {
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const { data: milestones = [] } = useLoadAll<Milestone>(skipioApi.milestones);

  async function sendMilestone(milestone: Milestone) {
    const data = {
      recipients: [`contact-${contactId}`],
      message: {
        messageType: "MilestoneMessage",
        milestoneId: milestone.id,
      },
    };

    await skipioApi.messages.post(
      {},
      {
        data,
      }
    );

    if (onMilestone) onMilestone(milestone);

    await queryClient.invalidateQueries({ queryKey: ["messages"] });
  }

  return (
    <MenuOverlay
      bottom
      trigger={({ show, ref }) => (
        <Button variant="link" onClick={show} ref={ref}>
          Milestone
        </Button>
      )}
      content={({ hide }) => (
        <>
          {milestones.map((milestone) => (
            <MenuButtonItem
              key={milestone.id}
              onClick={async () => {
                await sendMilestone(milestone);
                hide();
              }}
            >
              {milestone.name}
            </MenuButtonItem>
          ))}
        </>
      )}
    />
  );
}

export const Messages = memo(function Messages({
  contactId,
}: {
  contactId: string;
}) {
  const moment = useMoment();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const contact = useSkipioQuery({ queryKey: ["contacts", contactId] }).data;

  const {
    data,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ["messages", { contactId }],
    queryFn: ({ pageParam }) => fetchMessages(contactId, pageParam),
    getNextPageParam: (lastPage) => {
      if (!lastPage.meta || !lastPage.meta.hasMore) return;
      return {
        cursor: lastPage.data[lastPage.data.length - 1].id,
        direction: "forward",
      };
    },
  });
  const messages = data?.pages.flatMap((page) => page.data) || [];

  const getInitialFocusedNumber = () => {
    const assignedProviderNumber = user.providerPhoneNumbers[0]?.phoneNumber;
    const lastMessageWithNumber = messages.find(
      (message) => message.providerPhoneNumber
    );

    return (
      assignedProviderNumber ||
      lastMessageWithNumber?.providerPhoneNumber?.phoneNumber ||
      null
    );
  };
  const [focusedProviderNum, setFocusedProviderNum] = useState<string | null>(
    getInitialFocusedNumber
  );

  const scrollRef = useScrollTriggers(({ fromTop }) => {
    setAtBottom(fromTop >= 0);
  });

  const { ref: loaderRef, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView]);

  const isStackable = (
    messageA: Message,
    messageB: Message,
    timeInterval: number
  ) =>
    messageA &&
    messageB &&
    messageA.messageType === messageB.messageType &&
    messageA.direction === messageB.direction &&
    messageA.authorId === messageB.authorId &&
    messageA.providerPhoneNumber?.phoneNumber ===
      messageB.providerPhoneNumber?.phoneNumber &&
    Math.abs(Date.parse(messageA.time) - Date.parse(messageB.time)) <
      timeInterval;

  useNotificationPrevention(contact.id);

  return (
    <MessageBoxContent
      atBottom={atBottom}
      ref={(el) => {
        contentRef.current = el;
        scrollRef.current = el;
      }}
    >
      {messages.map((message, index) => {
        const nextMessage = messages[index - 1];
        const prevMessage = messages[index + 1];

        const stackedWithPrevMessage = isStackable(
          message,
          prevMessage,
          ms("5m")
        );
        const stackedWithNextMessage = isStackable(
          message,
          nextMessage,
          ms("5m")
        );

        let author;
        let metadata;
        let primaryAvatar;
        let secondaryAvatar;

        metadata = message.direction === "outbound" ? "from " : "to ";
        metadata += message.providerPhoneNumber
          ? `${
              message.providerPhoneNumber.label ||
              formatPhoneNumber(message.providerPhoneNumber?.phoneNumber)
            }`
          : "unknown number";
        metadata += ` @ ${moment(message.time).format("LT")}`;

        if (message.messageType === "ComplianceSmsMessage") {
          author = "Skipio";
          primaryAvatar = { icon: <SkipioLogo /> };
        } else if (message.meta?.autoResponder) {
          author = "Auto Reply";
          primaryAvatar = { icon: <Reply /> };
        } else if (
          message.meta?.birthday ||
          message.campaignId === "birthday"
        ) {
          author = "Birthday Message";
          primaryAvatar = { icon: <Birthday /> };
        } else if (message.campaignId && message.campaignId !== "birthday") {
          author = message.campaign?.name || "Campaign";
          primaryAvatar = { icon: <MegaPhone />, tooltip: "Campaign" };
          if (message.user) {
            secondaryAvatar = {
              imageUrl: message.user.avatarUrl,
              initials: `${(message.user.firstName || " ")[0]}${
                (message.user.lastName || " ")[0]
              }`,
              tooltip: `${message.user.firstName} ${message.user.lastName}`,
            };
          }
        } else if (message.messageType === "InboundSmsMessage") {
          primaryAvatar = {
            initials: `${(contact.firstName || " ")[0]}${
              (contact.lastName || " ")[0]
            }`,
            tooltip: `${contact.firstName} ${contact.lastName}`,
          };
        } else if (message.direction === "outbound" && message.user) {
          author = `${message.user.firstName} ${message.user.lastName}`;
          primaryAvatar = {
            imageUrl: message.user.avatarUrl,
            initials: `${(message.user.firstName || " ")[0]}${
              (message.user.lastName || " ")[0]
            }`,
            tooltip: `${message.user.firstName} ${message.user.lastName}`,
          };
          if (message.trueUser && message.user.id !== message.trueUser.id) {
            author = `${message.trueUser.firstName} ${message.trueUser.lastName} (as ${message.user.firstName} ${message.user.lastName})`;
            secondaryAvatar = {
              imageUrl: message.trueUser.avatarUrl,
              initials: `${(message.trueUser.firstName || " ")[0]}${
                (message.trueUser.lastName || " ")[0]
              }`,
              tooltip: `${message.trueUser.firstName} ${message.trueUser.lastName}`,
            };
          }
        } else {
          author = "Unknown Sender";
          primaryAvatar = {
            initials: "??",
            tooltip: "Unknown Sender",
          };
        }

        const focusButton = {
          icon: <Eye />,
          onClick: () =>
            message.providerPhoneNumber &&
            setFocusedProviderNum(message.providerPhoneNumber.phoneNumber),
          tooltip: `Focus messages to/from ${
            message.providerPhoneNumber
              ? formatPhoneNumber(message.providerPhoneNumber?.phoneNumber)
              : "Unknown Number"
          }`,
        };
        let messageElement;
        if (message.messageType === "MilestoneMessage") {
          messageElement = (
            <MilestoneAlert sound={false} confetti={false}>
              <Title>
                {message.milestone ? message.milestone.name : "Milestone"}
                <small>{moment(message.time).format("llll")}</small>
              </Title>
            </MilestoneAlert>
          );
        } else if (message.messageType === "SystemMessage") {
          messageElement = (
            <SystemEvent data-ignore="true">
              <span>{message.body}</span>
            </SystemEvent>
          );
        } else {
          const sharedMessageProps = {
            author,
            focusButton,
            direction: message.direction,
            focused: focusedProviderNum
              ? message.providerPhoneNumber?.phoneNumber === focusedProviderNum
              : true,
            metadata,
            primaryAvatar,
            secondaryAvatar,
            stackedWithNextMessage,
            stackedWithPrevMessage,
          };

          if (message.media.length) {
            messageElement = (
              <>
                {message.media.map((mediaContent, index) => (
                  <MediaMessage
                    {...sharedMessageProps}
                    mediaType={mediaContent.type}
                    mediaUrl={mediaContent.url}
                    stackedWithNextMessage={
                      stackedWithNextMessage ||
                      !!message.body ||
                      index < message.media.length - 1
                    }
                    stackedWithPrevMessage={stackedWithPrevMessage || index > 0}
                  />
                ))}
                {message.body && (
                  <SmsMessage
                    {...sharedMessageProps}
                    stackedWithPrevMessage={true}
                  >
                    {message.body}
                  </SmsMessage>
                )}
              </>
            );
          } else if (message.voicemailId) {
            messageElement = (
              <VoicemailMessage
                {...sharedMessageProps}
                voicemailId={message.voicemailId}
              />
            );
          } else {
            messageElement = (
              <SmsMessage {...sharedMessageProps}>{message.body}</SmsMessage>
            );
          }
        }

        const showTimestamp =
          !prevMessage ||
          message.messageType === "SystemMessage" ||
          (message.messageType !== "MilestoneMessage" &&
            Math.abs(Date.parse(message.time) - Date.parse(prevMessage.time)) >
              ms("30m"));

        return (
          <div key={message.id}>
            {showTimestamp && (
              <Timestamp>{moment(message.time).format("llll")}</Timestamp>
            )}
            {messageElement}
          </div>
        );
      })}
      {messages.length > 0 && hasNextPage && (
        <div>
          <Loader ref={loaderRef} />
        </div>
      )}
    </MessageBoxContent>
  );
});

const MessageBoxContent = styled(BoxContent)<{ atBottom: boolean }>`
  display: flex;
  flex-direction: column-reverse;
  border-bottom: 1px solid ${(p) => (p.atBottom ? "#0000" : "#d7d7d9")};
`;

const SystemEvent = styled.div`
  padding: 12px 0;
  text-align: center;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 12px;
  color: #7f7f7f;
  max-width: 30vw;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: auto;
  white-space: pre-wrap;
  & > span {
    background: #00000008;
    border: 1px solid #e9e9e9;
    display: inline-block;
    border-radius: 100px;
    padding: 8px 20px;
  }
`;

const Timestamp = styled.time`
  font-size: 12px;
  color: #888;
  text-align: center;
  margin: 20px 0;
  display: block;
`;

const FullHeight = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
